import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api, query } from '../lib/api/client';
import type { LeituraDeConsumidor, PaginaDeLeituras } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';
import { emDataHora } from '../lib/format/datetime';

/**
 * As leituras de consumidor (A19) — uma a uma, e só pelo prazo em que o detalhe existe.
 *
 * <h3>Não é a linha do tempo do item</h3>
 * Leitura de consumidor não é evento de cadeia (ADR-024): qualquer pessoa com o link gera uma.
 * Por isso ela mora aqui, e não na página do item nem no passaporte.
 *
 * <h3>A cidade diz que é aproximada, na própria célula</h3>
 * Vem do IP (ADR-124). IP de celular aponta para o concentrador da operadora: a UF costuma
 * acertar, a cidade erra com frequência. Uma coluna "Cidade" sem esse aviso seria lida como
 * endereço.
 *
 * <h3>O filtro mora na URL</h3>
 * Mesma razão da busca de itens: "olha estas leituras com lacre aberto" precisa ser um link.
 */
export function LeiturasDeConsumidorPage() {
  const { traduzir, vocabulario } = useAuth();
  const [params, setParams] = useSearchParams();

  const dias = Number(params.get('days') ?? '30');
  const resultado = params.get('result') ?? '';
  const via = params.get('via') ?? '';
  const lacreAberto = params.get('sealOpen') === 'true';
  const pagina = Number(params.get('page') ?? '0');

  const leituras = useQuery({
    queryKey: ['leituras-de-consumidor', dias, resultado, via, lacreAberto, pagina],
    queryFn: () =>
      api.get<PaginaDeLeituras>(
        `/console/consumer-scans${query({
          days: dias,
          result: resultado,
          via,
          sealOpen: lacreAberto ? 'true' : undefined,
          page: pagina,
          size: 50,
        })}`,
      ),
  });

  // Os mesmos filtros da tela, sem a página: o arquivo leva tudo (ADR-129).
  const exportar = useMutation({
    mutationFn: () =>
      api.baixar(
        `/console/consumer-scans/export${query({
          days: dias,
          result: resultado,
          via,
          sealOpen: lacreAberto ? 'true' : undefined,
        })}`,
      ),
  });

  function trocar(chave: string, valor: string) {
    const novo = new URLSearchParams(params);
    if (valor) {
      novo.set(chave, valor);
    } else {
      novo.delete(chave);
    }
    novo.delete('page');
    setParams(novo);
  }

  function trocarPagina(nova: number) {
    const novo = new URLSearchParams(params);
    novo.set('page', String(Math.max(nova, 0)));
    setParams(novo);
  }

  const d = leituras.data;

  return (
    <>
      <PageHeader
        titulo="Leituras de consumidor"
        subtitulo="Quem abriu o passaporte de um produto seu. Não entram na linha do tempo do item nem no passaporte."
        acoes={
          <button
            className="btn btn-secondary"
            disabled={exportar.isPending || !d || d.total === 0}
            onClick={() => exportar.mutate()}
            title="Os filtros da tela, em CSV. Cada exportação fica registrada."
          >
            {exportar.isPending ? 'Gerando…' : 'Exportar CSV'}
          </button>
        }
      />
      {exportar.isError && (
        <div className="wrap" style={{ paddingBottom: 0 }}>
          <Erro erro={exportar.error} />
        </div>
      )}
      <div className="wrap">
        <div className="filtros">
          <div className="campo">
            <label htmlFor="dias">Período</label>
            <select id="dias" value={String(dias)} onChange={(e) => trocar('days', e.target.value)}>
              <option value="1">Últimas 24 horas</option>
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="180">Últimos 180 dias</option>
              <option value="365">Últimos 12 meses</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="res">Resultado</label>
            <select id="res" value={resultado} onChange={(e) => trocar('result', e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(vocabulario.consumerScan ?? {}).map(([codigo, texto]) => (
                <option key={codigo} value={codigo}>
                  {texto}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="via">Como chegou</label>
            <select id="via" value={via} onChange={(e) => trocar('via', e.target.value)}>
              <option value="">QR ou NFC</option>
              {Object.entries(vocabulario.consumerVia ?? {}).map(([codigo, texto]) => (
                <option key={codigo} value={codigo}>
                  {texto}
                </option>
              ))}
            </select>
          </div>
          <label className="campo" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              style={{ width: 16, height: 16, margin: 0 }}
              checked={lacreAberto}
              onChange={(e) => trocar('sealOpen', e.target.checked ? 'true' : '')}
            />
            Só lacre aberto
          </label>
        </div>

        {leituras.isPending && <Carregando o="as leituras" />}
        {leituras.isError && <Erro erro={leituras.error} />}
        {d && d.conteudo.length === 0 && (
          <Vazio
            titulo="Nenhuma leitura neste período"
            detalhe="Uma linha aparece quando alguém abre o passaporte de um produto seu, pelo QR ou pelo toque na etiqueta."
          />
        )}

        {d && d.conteudo.length > 0 && (
          <div className="card tbl">
            <table className="t">
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Item</th>
                  <th>Produto</th>
                  <th>Resultado</th>
                  <th>Etiqueta</th>
                  <th>Região (aprox.)</th>
                  <th>Via</th>
                  <th>Origem</th>
                </tr>
              </thead>
              <tbody>
                {d.conteudo.map((l, n) => (
                  <tr key={`${l.quando}-${n}`}>
                    <td className="t-cap">{emDataHora(l.quando)}</td>
                    <td className="mono-s">
                      {l.itemId && l.serial ? (
                        <Link to={`/itens/${l.itemId}`}>{l.serial}</Link>
                      ) : (
                        <span className="dash">—</span>
                      )}
                    </td>
                    <td>{l.produto ?? <span className="dash">—</span>}</td>
                    <td>
                      <span className={corDoResultado(l.resultado)}>
                        {traduzir('consumerScan', l.resultado)}
                      </span>
                    </td>
                    <td>
                      <Etiqueta l={l} traduzir={traduzir} />
                    </td>
                    <td>{regiao(l)}</td>
                    <td>
                      {l.via ? (
                        <span className={l.via === 'NFC' ? 'chip chip-teal' : 'chip chip-neutral'}>
                          {traduzir('consumerVia', l.via)}
                        </span>
                      ) : (
                        <span className="dash">—</span>
                      )}
                    </td>
                    <td className="t-cap">{l.origem ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pager">
              <span>
                {d.total} leitura(s) · página {d.pagina + 1}
              </span>
              <div className="sp" style={{ flex: 1 }} />
              <button
                className="btn btn-secondary"
                disabled={pagina === 0}
                onClick={() => trocarPagina(pagina - 1)}
              >
                Anterior
              </button>
              <button
                className="btn btn-secondary"
                disabled={(pagina + 1) * d.tamanho >= d.total}
                onClick={() => trocarPagina(pagina + 1)}
              >
                Próxima
              </button>
            </div>
          </div>
        )}

        <div className="card cd" style={{ marginTop: 16 }}>
          <div className="cd2-t">Por que fica separado, e o que não guardamos</div>
          <div className="t-small" style={{ lineHeight: 1.55 }}>
            Leitura de consumidor <b>não é evento de cadeia</b>: não é assinada por elo credenciado e
            tem confiança baixíssima. Serve a antifraude e a analytics, e é visível só aqui. Não há
            cadastro, login nem permissão de localização. A região vem do endereço de rede, que{' '}
            <b>não é guardado</b>; ela aponta para a operadora, e não para quem leu — a UF costuma
            acertar, a cidade nem sempre. A leitura perde cidade e UF em{' '}
            <b>{d?.lugarEmDias ?? 180} dias</b> e some em <b>{d?.retencaoEmMeses ?? 12} meses</b>; o
            total por dia, produto, UF e cidade continua nos indicadores.
          </div>
          <div className="t-cap" style={{ marginTop: 10 }}>
            Localização por IP: <a href="https://db-ip.com">DB-IP.com</a>, licença CC BY 4.0.
          </div>
        </div>
      </div>
    </>
  );
}

function corDoResultado(resultado: string): string {
  switch (resultado) {
    case 'OK':
      return 'chip chip-neutral';
    case 'NOT_ACTIVE':
      return 'chip chip-warn';
    default:
      return 'chip chip-danger';
  }
}

function Etiqueta({
  l,
  traduzir,
}: {
  l: LeituraDeConsumidor;
  traduzir: (familia: string, valor: string | null | undefined) => string;
}) {
  if (!l.verificacao) {
    return <span className="dash">—</span>;
  }
  return (
    <>
      <span className={l.verificacao === 'VERIFIED' ? 'chip chip-teal' : 'chip chip-warn'}>
        {traduzir('tagCheck', l.verificacao)}
      </span>
      {l.lacre && (
        <span
          className={l.lacre === 'OPEN' ? 'chip chip-danger' : 'chip chip-neutral'}
          style={{ marginLeft: 4 }}
        >
          {traduzir('seal', l.lacre)}
        </span>
      )}
    </>
  );
}

function regiao(l: LeituraDeConsumidor): string {
  if (l.cidade && l.uf) {
    return `${l.cidade}/${l.uf}`;
  }
  if (l.uf) {
    return l.pais && l.pais !== 'BR' ? `${l.uf} · ${l.pais}` : l.uf;
  }
  return l.pais ?? '—';
}
